import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CheckBox,
  Select,
  Tabs,
  TopProgress,
} from "../../../../components";
import { clientService, permissionsService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import useDebounce from "../../../../hooks/useDebounce";
import PermissionsSelector from "./PermissionsSelector";
import {
  ActionsRow,
  Card,
  EmptyState,
  InfoBox,
  InfoText,
  InfoTitle,
  InlineLink,
  List,
  PermissionsWrapper,
  SectionTitle,
  SelectionRow,
  TabsWrapper,
} from "../styles";
import {
  flattenPermissions,
  PermissionsByCategory,
  SelectorOption,
  UserPermissions,
} from "../types";

const UsersTab: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [userOptions, setUserOptions] = useState<SelectorOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectorOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userPermissions, setUserPermissions] =
    useState<UserPermissions | null>(null);
  const [activePanel, setActivePanel] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [hasUsers, setHasUsers] = useState<boolean>(true);
  const debouncedSearch = useDebounce(search, 300);

  const mapUserToOption = useCallback(
    (user: any): SelectorOption => ({
      id: user.id,
      value: user.id,
      label:
        user.email ||
        user.display ||
        user.full_name ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        `#${user.id}`,
    }),
    []
  );

  const loadUsers = useCallback(
    async (
      searchValue: string,
      fallbackOption?: SelectorOption | null,
      updateAvailability?: boolean
    ) => {
      try {
        const response = await clientService.getUsers({
          page: 1,
          pageSize: 20,
          ordering: "email",
          search: searchValue || "",
          is_superuser: false,
        });
        const objects = response?.data?.objects || [];
        if (updateAvailability) {
          const total = response?.data?.count ?? objects.length;
          setHasUsers(Boolean(total));
        }
        const mapped = objects.map((user: any) => mapUserToOption(user));
        if (
          fallbackOption &&
          !mapped.some((option: SelectorOption) => option.id === fallbackOption.id)
        ) {
          mapped.push(fallbackOption);
        }
        setUserOptions(mapped);
      } catch (error) {
        console.error("Failed to load users", error);
        if (updateAvailability) {
          setHasUsers(false);
        }
      }
    },
    [mapUserToOption]
  );

  useEffect(() => {
    loadUsers(debouncedSearch, selectedOption, !search);
  }, [debouncedSearch, loadUsers, selectedOption, search]);

  useEffect(() => {
    if (!userPermissions) return;
    const panelKeys = Object.keys(userPermissions.permissions || {});
    if (panelKeys.length && !panelKeys.includes(activePanel)) {
      setActivePanel(panelKeys[0]);
    }
  }, [userPermissions, activePanel]);

  const handleUserChange = useCallback(
    async (option: SelectorOption | string | number | null) => {
      const optionObject =
        option && typeof option === "object" && "id" in option
          ? (option as SelectorOption)
          : userOptions.find(
              (item) =>
                item.id === option ||
                item.value === option ||
                item.label === option
            ) || null;

      const selectedId =
        (optionObject && (optionObject.id ?? optionObject.value)) ||
        (typeof option === "string" || typeof option === "number"
          ? option
          : null);

      setSelectedOption(optionObject);
      setSelectedUser(null);
      setUserPermissions(null);
      setActivePanel("");

      if (!selectedId) {
        return;
      }

      setLoading(true);
      try {
        const userRes = await clientService.getUsersById(String(selectedId));
        const userData = userRes?.data;
        setSelectedUser(userData);
        if (userData && !userData.is_superuser) {
          const permissionsRes = await permissionsService.getUserPermissions(
            String(selectedId)
          );
          const permissionsData = permissionsRes?.data as UserPermissions;
          setUserPermissions(permissionsData);
          const firstPanel = Object.keys(permissionsData?.permissions || {})[0];
          if (firstPanel) {
            setActivePanel(firstPanel);
          }
        }
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            t("FAILED_TO_LOAD_DATA"),
        });
      } finally {
        setLoading(false);
      }
    },
    [t, viewAlert, userOptions]
  );

  const panelTabs = useMemo(() => {
    if (!userPermissions?.permissions) {
      return [];
    }
    return Object.keys(userPermissions.permissions).map((panelKey: string) => ({
      title:
        userPermissions.panel_display_names?.[panelKey] ||
        panelKey.replace(/_/g, " "),
      slug: panelKey,
      id: panelKey,
    }));
  }, [userPermissions]);

  const handlePanelPermissionsChange = useCallback(
    (panelKey: string, updated: PermissionsByCategory) => {
      setUserPermissions((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [panelKey]: updated,
          },
        };
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!selectedUser) {
      viewAlert({
        severity: "error",
        message: t("PLEASE_SELECT_USER"),
      });
      return;
    }
    if (!userPermissions) {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...userPermissions,
        permissions: flattenPermissions(userPermissions.permissions),
      };
      await permissionsService.saveUserPermissions(payload);
      viewAlert({
        severity: "success",
        message: t("AUTHORIZATION_USER_PERMISSIONS_SAVED"),
      });
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setSaving(false);
    }
  }, [selectedUser, userPermissions, t, viewAlert]);

  if (!hasUsers) {
    return (
      <Card>
        <SectionTitle>{t("AUTHORIZATION_SELECT_USER_TITLE")}</SectionTitle>
        <EmptyState>{t("AUTHORIZATION_NO_USERS")}</EmptyState>
      </Card>
    );
  }

  const userGroups = selectedUser?.user_groups || [];
  const permissionsForPanel =
    activePanel && userPermissions?.permissions?.[activePanel];
  const effectiveForPanel =
    activePanel && userPermissions?.effective_permissions?.[activePanel];

  return (
    <Card>
      <TopProgress loading={loading || saving} />
      <SectionTitle>{t("AUTHORIZATION_SELECT_USER_TITLE")}</SectionTitle>
      <SelectionRow>
        <Select
          data={userOptions}
          selectedValue={selectedOption?.id || selectedOption?.value || ""}
          onChange={(option: any) => handleUserChange(option)}
          hasSearch
          onSearchChange={setSearch}
          title={t("USER")}
          placeholder={t("AUTHORIZATION_SELECT_USER_PLACEHOLDER")}
        />
      </SelectionRow>

      {selectedUser && (
        <InfoBox>
          <InfoTitle>{t("AUTHORIZATION_NOTE_TITLE")}</InfoTitle>
          <InfoText>{t("AUTHORIZATION_GROUP_NOTE")}</InfoText>
          {userGroups.length ? (
            <>
              <InfoText>
                {t("AUTHORIZATION_USER_IN_GROUPS", { user: selectedUser.email })}
              </InfoText>
              <List>
                {userGroups.map((group: any) => (
                  <li key={group.id}>
                    <InlineLink
                      to={`/clients-users/user-groups/${group.id}/edit`}
                    >
                      {group.name || `#${group.id}`}
                    </InlineLink>
                  </li>
                ))}
              </List>
            </>
          ) : (
            <InfoText>
              {t("AUTHORIZATION_USER_NOT_IN_GROUPS", {
                user: selectedUser.email,
              })}
            </InfoText>
          )}
        </InfoBox>
      )}

      {selectedUser?.is_superuser && (
        <InfoBox>
          <InfoTitle>{t("AUTHORIZATION_SUPERUSER_TITLE")}</InfoTitle>
          <InfoText>{t("AUTHORIZATION_SUPERUSER_TEXT")}</InfoText>
          <InfoText>{t("AUTHORIZATION_SUPERUSER_HINT")}</InfoText>
        </InfoBox>
      )}

      {!selectedUser?.is_superuser && userPermissions && (
        <>
          <CheckBox
            title={t("AUTHORIZATION_GRANT_IMPLICITLY")}
            value={userPermissions.implicitly_granted}
            onChange={() =>
              setUserPermissions((prev) =>
                prev
                  ? {
                      ...prev,
                      implicitly_granted: !prev.implicitly_granted,
                    }
                  : prev
              )
            }
          />
          <PermissionsWrapper>
            <TabsWrapper>
              <Tabs
                tabs={panelTabs}
                size="display"
                blockNavigate
                location={activePanel}
                onChange={(panel) => setActivePanel(panel)}
              />
            </TabsWrapper>
            {permissionsForPanel ? (
              <PermissionsSelector
                permissions={permissionsForPanel}
                effectivePermissions={effectiveForPanel || undefined}
                onChange={(updated) =>
                  handlePanelPermissionsChange(activePanel, updated)
                }
              />
            ) : (
              <EmptyState>{t("AUTHORIZATION_NO_PERMISSIONS_FOUND")}</EmptyState>
            )}
          </PermissionsWrapper>
          <ActionsRow>
            <Button
              size="mid"
              title={t("AUTHORIZATION_SAVE_PERMISSIONS")}
              onClick={handleSave}
              disabled={saving || !userPermissions}
            />
          </ActionsRow>
        </>
      )}
    </Card>
  );
};

export default UsersTab;
